import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useAuth } from "react-oidc-context";
import { createApiClient, type FarmacenterApi } from "@farmacenter/api";
import { config } from "../config";

const ApiContext = createContext<FarmacenterApi | null>(null);

/**
 * Pone a disposición un cliente de API que siempre lleva el token vigente.
 *
 * El token no se copia: el cliente lo pide en cada petición a través de `getToken`,
 * que lee el usuario actual de la sesión. Cuando el refresco silencioso renueva el
 * token, la siguiente petición ya usa el nuevo sin que nadie tenga que recrear nada.
 */
export function ApiProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();

  const client = useMemo(
    () =>
      createApiClient({
        baseUrl: config.apiBaseUrl,
        getToken: () => auth.user?.access_token ?? null,
      }),
    // Recrear sólo cuando cambia la identidad, no en cada refresco de token: el
    // getter lee `auth.user` en el momento de la petición, así que basta con que
    // el cierre apunte al `auth` actual.
    [auth],
  );

  return <ApiContext.Provider value={client}>{children}</ApiContext.Provider>;
}

export function useApi(): FarmacenterApi {
  const client = useContext(ApiContext);
  if (!client) {
    throw new Error("useApi debe usarse dentro de <ApiProvider>");
  }
  return client;
}
